import { fileEtag, directoryEtag } from './etag.js'

export class DirectoryListing {
    constructor(mimeTypeDetector) {
        this.mimeTypeDetector = mimeTypeDetector
    }

    async getDirectoryListingJSON(path, dirHandle, lockManager = null, entryFilter = null) {
        const entries = []

        try {
            for await (const entry of dirHandle.values()) {
                const childPath = path.endsWith('/')
                    ? path + entry.name
                    : path + '/' + entry.name

                if (entryFilter && (await entryFilter(entry.name, childPath, entry.kind)) !== true) continue // a status = refused: hidden here

                const isDir = entry.kind === 'directory'
                let size = 0, lastmod = new Date().toUTCString(), mime = ''

                if (!isDir) {
                    try {
                        const file = await entry.getFile()
                        size = file.size
                        lastmod = file.lastModified
                            ? new Date(file.lastModified).toUTCString()
                            : lastmod
                        mime = this.mimeTypeDetector.detectMimeType(entry.name)
                    } catch (error) {
                        console.warn(`Could not get file info for ${entry.name}:`, error)
                        mime = 'application/octet-stream'
                    }
                }

                entries.push({
                    filename: childPath,
                    basename: entry.name,
                    type: isDir ? 'directory' : 'file',
                    size,
                    lastmod,
                    mime,
                })
            }

            entries.sort((a, b) => {
                if (a.type !== b.type) return a.type === 'directory' ? -1 : 1
                return a.basename.localeCompare(b.basename)
            })
        } catch (error) {
            console.warn(`Error reading directory ${path}:`, error)
        }

        return entries
    }

    async getDirectoryListingJSONDetailed(path, dirHandle, xmlBuilder, entryFilter = null) {
        const listing = {
            href: path.endsWith('/') ? path : path + '/',
            properties: {
                displayname: dirHandle.name,
                resourcetype: 'collection',
                contenttype: 'httpd/unix-directory',
                contentlength: 0,
                lastmodified: new Date().toUTCString(),
                etag: await directoryEtag(dirHandle),
            },
            children: [],
        }

        try {
            for await (const entry of dirHandle.values()) {
                const childPath = path.endsWith('/')
                    ? path + entry.name
                    : path + '/' + entry.name

                // Apply entry filter (e.g. dotfile visibility)
                if (entryFilter && (await entryFilter(entry.name, childPath, entry.kind)) !== true) continue // a status = refused: hidden here

                const childInfo = {
                    href:
                        entry.kind === 'directory' && !childPath.endsWith('/')
                            ? childPath + '/'
                            : childPath,
                    properties: {
                        displayname: entry.name,
                        resourcetype:
                            entry.kind === 'directory'
                                ? 'collection'
                                : 'resource',
                    },
                }

                if (entry.kind === 'file') {
                    try {
                        const file = await entry.getFile()
                        childInfo.properties.etag = await fileEtag(file, childPath)
                        childInfo.properties.contenttype = this.mimeTypeDetector.detectMimeType(
                            entry.name
                        )
                        childInfo.properties.contentlength = file.size
                        childInfo.properties.lastmodified = file.lastModified
                            ? new Date(file.lastModified).toUTCString()
                            : new Date().toUTCString()
                    } catch (error) {
                        console.warn(
                            `Could not get file info for ${entry.name}:`,
                            error
                        )
                        childInfo.properties.contenttype =
                            'application/octet-stream'
                        childInfo.properties.contentlength = 0
                        childInfo.properties.lastmodified =
                            new Date().toUTCString()
                    }
                } else {
                    childInfo.properties.contenttype = 'httpd/unix-directory'
                    childInfo.properties.contentlength = 0
                    childInfo.properties.lastmodified = new Date().toUTCString()
                }

                listing.children.push(childInfo)
            }

            listing.children.sort((a, b) => {
                if (a.properties.resourcetype !== b.properties.resourcetype) {
                    return a.properties.resourcetype === 'collection' ? -1 : 1
                }
                return a.properties.displayname.localeCompare(
                    b.properties.displayname
                )
            })
        } catch (error) {
            console.warn(`Error reading directory ${path}:`, error)
        }

        return listing
    }
}